import React, { createContext, useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "./AuthContext";
import WebSocketService from "../services/WebSocketService";
import { queryClient } from "../config/queryClient";
import { deviceKeys } from "../hooks/useDevices";
import { printerKeys } from "../hooks/usePrinters";

const DEBUG_WS = process.env.NODE_ENV === 'development';

export const WebSocketContext = createContext();

// Mapowanie typów zdarzeń na funkcje obsługujące
const eventHandlers = {
  "device_update": (data) => {
    // Pokaż bardziej zwięzły log zamiast powtarzania całego payloadu
    if (DEBUG_WS) {
      const deviceId = data.device.uuid || 'nowe';
      console.log(`[WebSocketContext] Operacja ${data.operation} dla urządzenia ${deviceId}`);
    }
    
    // Pobierz aktualny stan cache
    const currentData = queryClient.getQueryData(deviceKeys.allDevices()) || [];
    
    // W zależności od operacji, aktualizuj cache inaczej
    switch(data.operation) {
      case "add":
        // Dodaj nowe urządzenie do istniejącego cache
        queryClient.setQueryData(deviceKeys.allDevices(), [...currentData, data.device]);
        if (DEBUG_WS) console.log("[WebSocketContext] Dodano urządzenie do cache");
        break;
        
      case "update":
        // Aktualizuj istniejące urządzenie w cache
        queryClient.setQueryData(deviceKeys.allDevices(), 
          currentData.map(device => {
            if (device.uuid === data.device.uuid) {
              // Głęboka aktualizacja - zachowaj istniejące właściwości i dodaj/nadpisz nowe
              return { 
                ...device, 
                ...data.device,
                // Specjalna obsługa zagnieżdżonego obiektu connection_status
                connection_status: {
                  ...device.connection_status,
                  ...data.device.connection_status
                }
              };
            }
            return device;
          })
        );
        if (DEBUG_WS) console.log("[WebSocketContext] Zaktualizowano urządzenie w cache");
        break;
        
      case "delete":
        // Usuń urządzenie z cache
        queryClient.setQueryData(deviceKeys.allDevices(), 
          currentData.filter(device => device.uuid !== data.device.uuid)
        );
        if (DEBUG_WS) console.log("[WebSocketContext] Usunięto urządzenie z cache");
        break;
        
      default:
        console.warn(`Nieobsługiwany typ operacji: ${data.operation}`);
        // Przy nieznanych operacjach po prostu unieważnij cache
        queryClient.invalidateQueries({ queryKey: deviceKeys.allDevices() });
    }
  },

  "printer_update": (data) => {
    if (DEBUG_WS) {
      const printerId = data.printer?.uuid || data.printer_uuid || 'nieznany';
      console.log(`[WebSocketContext] Aktualizacja drukarki ${printerId}`);
    }
    
    // Invalidate printer queries - safer approach for complex updates
    queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
    
    // If specific printer UUID is provided, also invalidate its detail
    if (data.printer_uuid) {
      queryClient.invalidateQueries({ queryKey: printerKeys.printer(data.printer_uuid) });
    }
  },

  "printer_added": (data) => {
    if (DEBUG_WS) {
      console.log(`[WebSocketContext] Dodano nową drukarkę: ${data.printer?.name || 'nieznana'}`);
    }
    
    queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
  },

  "printer_removed": (data) => {
    if (DEBUG_WS) {
      console.log(`[WebSocketContext] Usunięto drukarkę: ${data.printer_uuid || 'nieznana'}`);
    }
    
    queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
  },

  "printer_status_changed": (data) => {
    if (DEBUG_WS) {
      console.log(`[WebSocketContext] Zmiana statusu drukarki: ${data.printer_uuid || 'nieznana'}`);
    }
    
    queryClient.invalidateQueries({ queryKey: printerKeys.userPrinters() });
    
    if (data.printer_uuid) {
      queryClient.invalidateQueries({ queryKey: printerKeys.printer(data.printer_uuid) });
    }
  }
};

// Globalny stan WebSocket - poza komponentem
const webSocketState = {
  initialized: false,
  connected: false,
  subscribed: false,
  appIsUnmounting: false
};

export const WebSocketProvider = ({ children }) => {
  const [connectionState, setConnectionState] = useState("CLOSED");
  const { isAuthenticated, logout: authLogout } = useContext(AuthContext);
  const connectionCheckInterval = useRef(null);
  const subscriptions = useRef([]);

  // Inicjalizacja WebSocketService
  useEffect(() => {
    if (!webSocketState.initialized) {
      WebSocketService.initialize(authLogout);
      webSocketState.initialized = true;
    }

    // Aktualizuj stan połączenia regularnie
    connectionCheckInterval.current = setInterval(() => {
      const currentState = WebSocketService.getConnectionState();
      setConnectionState(currentState);
    }, 5000);

    return () => {
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }
    };
  }, [authLogout]);

  // Dla zarządzania połączeniem
  useEffect(() => {
    if (isAuthenticated && !webSocketState.connected) {
      console.log("Użytkownik uwierzytelniony. Nawiązuję połączenie WebSocket.");
      WebSocketService.connect();
      webSocketState.connected = true;
    } else if (!isAuthenticated && webSocketState.connected) {
      webSocketState.connected = false;
    }
  }, [isAuthenticated]);

  // Dla subskrypcji
  useEffect(() => {
    if (isAuthenticated && !webSocketState.subscribed) {
      // Dodaj log diagnostyczny
      console.log("Stan przed subskrypcją:", 
        { subscribedFlag: webSocketState.subscribed, currentSubs: subscriptions.current.length });
      
      // Podwójne zabezpieczenie - sprawdź także długość tablicy subskrypcji
      if (subscriptions.current.length > 0) {
        console.log("Wykryto istniejące subskrypcje, pomijam tworzenie nowych");
        webSocketState.subscribed = true;
        return;
      }
      
      // Utwórz nowe subskrypcje
      const unsubscribers = Object.keys(eventHandlers).map(eventType => {
        console.log(`Subskrybuję zdarzenie ${eventType} (jednorazowo)`);
        return WebSocketService.subscribe(eventType, eventHandlers[eventType]);
      });
      
      // Zapisz funkcje anulujące subskrypcje
      subscriptions.current = unsubscribers;
      webSocketState.subscribed = true;
      
      // Dodaj log potwierdzający zakończenie subskrypcji
      console.log("Subskrypcje zakończone, nowa liczba:", subscriptions.current.length);
    } else if (!isAuthenticated && webSocketState.subscribed) {
      console.log("Użytkownik wylogowany, czyszczę subskrypcje");
      subscriptions.current.forEach(unsubscribe => unsubscribe());
      subscriptions.current = [];
      webSocketState.subscribed = false;
    }
    
    // Funkcja cleanup do wywołania przy odmontowaniu komponentu
    return () => {
      // Dodaj warunek, aby komunikat pojawiał się tylko gdy było aktywne połączenie
      if (webSocketState.subscribed || subscriptions.current.length > 0) {
        if (webSocketState.appIsUnmounting) {
          console.log("Aplikacja jest odmontowywana, czyszczę subskrypcje");
          subscriptions.current.forEach(unsubscribe => unsubscribe());
          subscriptions.current = [];
          webSocketState.subscribed = false;
        } else {
          console.log("Pominięto czyszczenie subskrypcji - to prawdopodobnie efekt StrictMode");
        }
      }
    };
  }, [isAuthenticated]);

  // Wysyłanie wiadomości WebSocket
  const sendMessage = (message) => {
    return WebSocketService.send(message);
  };

  // Wylogowanie (rozłącza WebSocket i wywołuje authLogout)
  const logout = () => {
    WebSocketService.disconnect();
    authLogout();
  };

  // Ręczne odświeżenie danych na podstawie typu
  const refreshData = (dataType) => {
    switch(dataType) {
      case "devices":
        queryClient.invalidateQueries({ queryKey: deviceKeys.allDevices() });
        break;
      // Możesz dodać więcej typów danych
      default:
        console.warn(`Nieobsługiwany typ danych do odświeżenia: ${dataType}`);
    }
  };

  return (
    <WebSocketContext.Provider value={{
      connectionState,
      sendMessage,
      logout,
      refreshData,
      isConnected: connectionState === "OPEN"
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for easier access to WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};